import { FeatureLineup } from '@mdlbeasts/nofomo.ui.web';
import { useCallback, useMemo, useState } from 'react';

import { LINEUP_ENTRY_PARAM } from 'constants/lineup.constants';
import { useLineupRoute } from 'hooks/useLineupRoute';
import { useLocale } from 'hooks/useLocale';
import { useOverlayRoute } from 'hooks/useOverlayRoute';
import { useLineupEntries } from 'queries/lineup/useLineupEntries';

import LoadingScreen from 'components/@common/LoadingScreen';
import LineupEmpty from 'components/@lineup/LineupEmpty';
import LineupEntryOverlay from 'components/@lineup/LineupEntryOverlay';
import { toLineupTimeTableDays } from 'components/@lineup/normalizers';

/**
 * One day at a time, stages across and time down. Reads the same sets as the
 * artists view, so switching tabs costs nothing.
 */
const LineupStandardTimeTableContainer = () => {
  const { locale } = useLocale();
  const { entryId, settings } = useLineupRoute();
  const { open, close } = useOverlayRoute({ param: LINEUP_ENTRY_PARAM });
  const { entries, timezone, isLoading } = useLineupEntries();

  const [dayIndex, setDayIndex] = useState(0);

  const days = useMemo(
    () => toLineupTimeTableDays({ entries, locale, timezone, settings }),
    [entries, locale, settings, timezone],
  );

  // Clamped, so a late-arriving day cannot leave the index past the end.
  const day = days[Math.min(dayIndex, days.length - 1)];

  const previousDay = useCallback(
    () => setDayIndex((index) => Math.max(index - 1, 0)),
    [],
  );

  const nextDay = useCallback(() => setDayIndex((index) => index + 1), []);

  const actions = useMemo(
    () => ({
      // A chevron with no action is inert, which marks the ends.
      previousDay: dayIndex > 0 ? previousDay : undefined,
      nextDay: dayIndex < days.length - 1 ? nextDay : undefined,
      pressSet: open,
    }),
    [dayIndex, days.length, nextDay, open, previousDay],
  );

  if (isLoading) return <LoadingScreen />;

  if (!day) return <LineupEmpty messageId="lineup.timetable.empty" />;

  return (
    <>
      <FeatureLineup.Standard.TimeTable
        day={{ label: day.label, sublabel: day.sublabel }}
        stages={day.stages}
        sets={day.sets}
        actions={actions}
      />

      {!!entryId && <LineupEntryOverlay entryId={entryId} onClose={close} />}
    </>
  );
};

export default LineupStandardTimeTableContainer;
